// /en/apostille/$country — EN mirror of /apostille/$country (P31-D parity)
import QuotableStatsStrip from "@/components/QuotableStatsStrip";
import type { ApostilleDeepEntry } from "@/data/apostille-deep-en";
import CountryDeepContent from "@/components/CountryDeepContent";
import { createFileRoute, notFound, Link } from "@tanstack/react-router";
import { APOSTILLE_COUNTRIES, getApostilleCountry } from "@/data/apostille-countries";
import { consolidateGraph } from "@/lib/schema-graph";
import { aggregateRatingNode, reviewNodes } from "@/lib/programmatic-ratings";
import { speakableSchema } from "@/lib/aeo";
import { hreflangLinks, dynamicOgImage, ctaDesc, ctaTitle } from "@/lib/seo";
import { TrustLayer } from "@/components/TrustLayer";
import { FaqAccordion } from "@/components/seo/Blocks";
import ApostilleTripleKeyLinks from "@/components/ApostilleTripleKeyLinks";
import ApostilleReverseLinks from "@/components/ApostilleReverseLinks";
import { Stamp, ShieldCheck, FileCheck2, Clock, MessageCircle, Phone, ListChecks, AlertTriangle, BookOpen, ExternalLink, BadgeCheck } from "lucide-react";

const SITE = "https://nyclanguageinstitute.com";

export const Route = createFileRoute("/en/apostille/$country/")({
  loader: async ({ params }) => {
    const { APOSTILLE_DEEP_EN } = await import("@/data/apostille-deep-en");
    const deep: ApostilleDeepEntry | null = APOSTILLE_DEEP_EN[params.country] ?? null;
    if (!getApostilleCountry(params.country)) throw notFound();
    return { deep, slug: params.country };
  },
  head: ({ params, loaderData }) => {
    const c = getApostilleCountry(params.country);
    if (!c) return { meta: [{ title: ctaTitle("Country not found", "/en/apostille/$country", "en")}] };
    const deep = loaderData?.deep;
    const enPath = `/en/apostille/${c.slug}`;
    const thPath = `/apostille/${c.slug}`;
    const url = `${SITE}${enPath}`;
    const label = c.isHague ? "Apostille" : "Consular Legalization";
    const title = deep?.metaTitle ?? `${label} for Thai Documents → ${c.nameEn} ${c.flag} from THB ${c.priceFromTh.toLocaleString()} | NYC`;
    const desc = deep?.metaDescription ?? `${label} of Thai documents for use in ${c.nameEn} — certified translation + MFA Thailand + ${c.authority}. Turnaround ${c.turnaround}. From THB ${c.priceFromTh.toLocaleString()}.`;
    const og = dynamicOgImage({ title: `${label} → ${c.nameEn}`, subtitle: `Thai docs · ${c.authority}`, badge: "NYC Legal & Notary" });
    const baseGraph: { type: "application/ld+json"; children: string }[] = [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org", "@type": "Service", "@id": `${url}#service`,
          name: `${label} of Thai documents for ${c.nameEn}`,
          serviceType: c.isHague ? "Apostille Service" : "Consular Legalization Service",
          inLanguage: "en",
          areaServed: [{ "@type": "Country", name: "Thailand" }, { "@type": "Country", name: c.nameEn }],
          provider: { "@type": "LegalService", "@id": `${SITE}/#org`, name: "NYC Legal & Notary Service", url: SITE, telephone: "+66832494999" },
          offers: { "@type": "Offer", price: c.priceFromTh, priceCurrency: "THB", availability: "https://schema.org/InStock" },
          aggregateRating: aggregateRatingNode(url),
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org", "@type": "HowTo", "@id": `${url}#howto`,
          name: `How to ${label.toLowerCase()} Thai documents for use in ${c.nameEn}`,
          inLanguage: "en", totalTime: "P7D",
          step: deep
            ? deep.pipeline.map((s, i) => ({ "@type": "HowToStep", position: i + 1, name: s.step, text: s.detail }))
            : [
                { "@type": "HowToStep", position: 1, name: "Send documents", text: "Send scans via LINE @nycli or email — quote within 30 minutes." },
                { "@type": "HowToStep", position: 2, name: "Certified translation", text: "Sworn translator registered with the Thai Ministry of Justice translates TH↔EN or destination language." },
                { "@type": "HowToStep", position: 3, name: "MFA Thailand authentication", text: "We submit at the Department of Consular Affairs — Apostille (since 2024) or standard legalization." },
                ...(c.isHague ? [] : [{ "@type": "HowToStep", position: 4, name: "Consular Legalization", text: `Final stamp at the ${c.nameEn} Embassy in Bangkok.` }]),
                { "@type": "HowToStep", position: c.isHague ? 4 : 5, name: "Return delivery", text: "EMS worldwide or Grab within Bangkok." },
              ],
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org", "@type": "BreadcrumbList", "@id": `${url}#breadcrumb`,
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: `${SITE}/en` },
            { "@type": "ListItem", position: 2, name: "Apostille & Legalization", item: `${SITE}/en/apostille` },
            { "@type": "ListItem", position: 3, name: `${label} → ${c.nameEn}`, item: url },
          ],
        }),
      },
    ];

    if (deep) {
      baseGraph.push({
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org", "@type": "Article", "@id": `${url}#article`,
          headline: deep.metaTitle,
          description: deep.metaDescription,
          inLanguage: "en",
          mainEntityOfPage: url,
          author: { "@type": "Organization", name: "NYC Legal & Notary Service", url: SITE },
          publisher: { "@type": "Organization", "@id": `${SITE}/#org`, name: "NYC Legal & Notary Service", url: SITE },
          datePublished: "2026-06-17",
          dateModified: "2026-06-17",
          about: { "@type": "Country", name: c.nameEn },
        }),
      });
      baseGraph.push({
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org", "@type": "FAQPage", "@id": `${url}#faq`,
          inLanguage: "en",
          mainEntity: deep.faqs.map((f) => ({
            "@type": "Question", name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        }),
      });
    }

    return {
      meta: [
        { title: ctaTitle(title, "/en/apostille/$country", "en") }, { name: "description", content: ctaDesc(desc, "/en/apostille/$country", "en")},
        { property: "og:title", content: ctaTitle(title, "/en/apostille/$country", "en")}, { property: "og:description", content: ctaDesc(desc, "/en/apostille/$country", "en")},
        { property: "og:url", content: url }, { property: "og:locale", content: "en_US" },
        { property: "og:type", content: deep ? "article" : "website" },
        { property: "og:image", content: og }, { name: "twitter:image", content: og },
      ],
      links: [
        { rel: "canonical", href: url },
        ...hreflangLinks(thPath, enPath),
      ],
      scripts: [
        consolidateGraph([
          speakableSchema([".aeo-speakable", "h1", "h2"]),
          ...baseGraph,
          ...(deep ? [] : reviewNodes(url, `${url}#service`, 3).map((r) => ({ type: "application/ld+json" as const, children: JSON.stringify(r) }))),
        ]),
      ],
    };
  },
  component: EnApostilleCountry,
  notFoundComponent: () => (
    <main className="min-h-screen bg-navy-deep p-12 text-white">
      <h1 className="text-2xl font-bold">Country not found</h1>
      <Link to="/en/apostille" className="mt-4 inline-block text-gold underline">Back to Apostille hub</Link>
          <QuotableStatsStrip cluster="apostille" locale="en" variant="light" />
    </main>
  ),
});

function EnApostilleCountry() {
  const { slug } = Route.useLoaderData() as { slug: string };
  const c = getApostilleCountry(slug)!;
  const deep = (Route.useLoaderData() as { deep: ApostilleDeepEntry | null }).deep;
  const others = APOSTILLE_COUNTRIES.filter((x) => x.slug !== slug && x.region === c.region).slice(0, 6);
  const label = c.isHague ? "Apostille" : "Consular Legalization";

  return (
    <main className="min-h-screen bg-white">
      <section className="relative overflow-hidden bg-navy-deep text-white">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_25%_0%,hsl(var(--gold)/0.18),transparent_60%)]" aria-hidden />
        <div className="relative mx-auto max-w-5xl px-4 py-14 sm:px-6 sm:py-20">
          <nav aria-label="Breadcrumb" className="mb-5 text-[12px] text-white/80">
            <Link to="/en" className="hover:text-gold">Home</Link>
            <span className="px-1.5 text-white/30">/</span>
            <Link to="/en/apostille" className="hover:text-gold">Apostille</Link>
            <span className="px-1.5 text-white/30">/</span>
            <span className="text-white/85">{c.nameEn}</span>
          </nav>
          <div className="inline-flex items-center gap-2 rounded-full border border-gold/40 bg-gold/10 px-3 py-1 text-[11px] font-bold uppercase tracking-widest text-gold">
            <Stamp className="h-3.5 w-3.5" /> {label} · {c.nameEn}
          </div>
          <h1 className="mt-4 font-display text-3xl font-extrabold leading-tight tracking-tight sm:text-5xl">
            <span className="mr-3 text-4xl">{c.flag}</span>
            {label} of Thai documents for {c.nameEn}
          </h1>
          <p className="mt-4 max-w-3xl text-base text-white/85 sm:text-lg">
            {label} of Thai documents for use in {c.nameEn} — certified translation, MFA Thailand authentication{c.isHague ? "" : `, and ${c.nameEn} Embassy legalization in Bangkok`}. Accepted by {c.authority}.
          </p>

          <dl className="mt-7 grid grid-cols-2 gap-3 sm:grid-cols-4">
            {[
              { k: "Method", v: label },
              { k: "From", v: `THB ${c.priceFromTh.toLocaleString()}` },
              { k: "Turnaround", v: c.turnaround },
              { k: "Accepting authority", v: c.authority },
            ].map((s) => (
              <div key={s.k} className="rounded-2xl border border-white/10 bg-white/[0.04] p-3 backdrop-blur">
                <dt className="text-[10px] font-bold uppercase tracking-widest text-gold">{s.k}</dt>
                <dd className="mt-1 text-sm font-extrabold text-white sm:text-base">{s.v}</dd>
              </div>
            ))}
          </dl>

          <div className="mt-7 flex flex-wrap gap-2.5">
            <a href="https://line.me/ti/p/@nycli" target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 rounded-full bg-emerald-600 px-5 py-2.5 text-sm font-bold text-white shadow-md hover:-translate-y-0.5 transition">
              <MessageCircle className="h-4 w-4" /> Chat on LINE
            </a>
            <a href="tel:+66832494999" className="inline-flex items-center gap-2 rounded-full bg-gold px-5 py-2.5 text-sm font-bold text-navy-deep shadow-md hover:-translate-y-0.5 transition">
              <Phone className="h-4 w-4" /> +66 83 249 4999
            </a>
          </div>
        </div>
      </section>

      <div className="mx-auto max-w-5xl px-4 py-12 sm:px-6 sm:py-14">
        <blockquote className="aeo-speakable rounded-2xl border-l-4 border-gold bg-amber-50/60 p-5 text-[14px] leading-relaxed text-slate-800 sm:text-[15px]">
          <span className="mr-2 inline-flex items-center gap-1 rounded-full bg-gold/20 px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest text-amber-800">
            <BadgeCheck className="h-3 w-3" /> Quotable
          </span>
          {deep?.speakable ?? `NYC Legal & Notary Service (DBD 0435567000061, est. 2010) handles ${label} of Thai documents for ${c.nameEn} (${c.nameTh}) end-to-end: certified translation, MoJ-registered translator stamp, MFA Thailand${c.isHague ? " Apostille" : ` plus ${c.nameEn} Embassy legalization`}. From THB ${c.priceFromTh.toLocaleString()}, ready in ${c.turnaround}.`}
        </blockquote>

        {deep && (
          <>
            <section className="mt-10">
              <h2 className="font-display text-2xl font-extrabold text-navy-deep sm:text-3xl">
                {c.nameEn} Authority Requirements
              </h2>
              <p className="mt-4 text-[15px] leading-relaxed text-slate-700">{deep.overview}</p>
              <div className="mt-5 rounded-2xl border border-amber-200 bg-amber-50/60 p-4 text-[14px] text-slate-800">
                <strong className="text-amber-800">Validity window:</strong> {deep.acceptanceWindow}
              </div>
            </section>

            <section className="mt-12">
              <h2 className="flex items-center gap-2 font-display text-2xl font-extrabold text-navy-deep sm:text-3xl">
                <ListChecks className="h-6 w-6 text-amber-600" />
                End-to-End Apostille Pipeline ({c.nameEn})
              </h2>
              <ol className="mt-5 space-y-3">
                {deep.pipeline.map((s, i) => (
                  <li key={i} className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
                    <div className="font-bold text-navy-deep">{s.step}</div>
                    <div className="mt-1 text-[14px] text-slate-700">{s.detail}</div>
                  </li>
                ))}
              </ol>
            </section>

            <section className="mt-12">
              <h2 className="font-display text-2xl font-extrabold text-navy-deep sm:text-3xl">
                Document Matrix — What & Why
              </h2>
              <div className="mt-5 overflow-hidden rounded-2xl border border-slate-200">
                <table className="w-full text-left text-[14px]">
                  <thead className="bg-slate-50 text-navy-deep">
                    <tr>
                      <th className="px-4 py-3">Document</th>
                      <th className="px-4 py-3">Required For</th>
                      <th className="px-4 py-3">Note</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-200">
                    {deep.docMatrix.map((v, i) => (
                      <tr key={i} className="bg-white">
                        <td className="px-4 py-3 font-semibold text-slate-800">{v.doc}</td>
                        <td className="px-4 py-3 text-slate-700">{v.need}</td>
                        <td className="px-4 py-3 text-slate-600">{v.note}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>

            <section className="mt-12">
              <h2 className="flex items-center gap-2 font-display text-2xl font-extrabold text-navy-deep sm:text-3xl">
                <AlertTriangle className="h-6 w-6 text-rose-600" />
                Common Pitfalls We Eliminate
              </h2>
              <div className="mt-5 grid gap-3 sm:grid-cols-2">
                {deep.pitfalls.map((p, i) => (
                  <div key={i} className="rounded-2xl border border-rose-200 bg-rose-50/40 p-4">
                    <div className="font-bold text-rose-900">{p.title}</div>
                    <div className="mt-1 text-[14px] text-slate-700">{p.detail}</div>
                  </div>
                ))}
              </div>
            </section>

            <section className="mt-12">
              <h2 className="font-display text-2xl font-extrabold text-navy-deep sm:text-3xl">FAQ — {c.nameEn} Apostille</h2>
              <div className="mt-5">
                <FaqAccordion items={deep.faqs} />
              </div>
            </section>

            {deep.citations.length > 0 && (
              <section className="mt-12 border-t border-slate-200 pt-10">
                <h2 className="flex items-center gap-2 font-display text-2xl font-extrabold text-navy-deep sm:text-3xl">
                  <BookOpen className="h-6 w-6 text-amber-600" />
                  Official Sources & Citations
                </h2>
                <ul className="mt-5 space-y-2 text-[14px]">
                  {deep.citations.map((cit, i) => (
                    <li key={i}>
                      <a href={cit.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 font-semibold text-amber-700 underline-offset-2 hover:underline">
                        {cit.label}
                        <ExternalLink className="h-3.5 w-3.5" />
                      </a>
                    </li>
                  ))}
                </ul>
              </section>
            )}
          </>
        )}

        <section className="mt-12">
          <h2 className="font-display text-2xl font-extrabold text-navy-deep sm:text-3xl">Common documents {label.toLowerCase()}d for {c.nameEn}</h2>
          <ul className="mt-5 grid gap-3 sm:grid-cols-2">
            {c.commonUses.map((u) => (
              <li key={u} className="flex items-start gap-2.5 rounded-xl border border-slate-200 bg-slate-50/60 p-3.5 text-sm text-slate-800">
                <FileCheck2 className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" /> {u}
              </li>
            ))}
          </ul>
        </section>

        <section className="mt-12">
          <h2 className="font-display text-2xl font-extrabold text-navy-deep sm:text-3xl">{label} workflow</h2>
          <ol className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {[
              { n: 1, t: "Send documents", d: "LINE / Email" },
              { n: 2, t: "Certified translation", d: "MoJ-registered translator" },
              { n: 3, t: c.isHague ? "MFA Apostille" : "MFA + Embassy", d: c.isHague ? "Consular Affairs" : `${c.nameEn} Embassy BKK` },
              { n: 4, t: "Return delivery", d: "EMS / Grab" },
            ].map((s) => (
              <li key={s.n} className="rounded-2xl border border-slate-200 bg-white p-4">
                <div className="text-[10px] font-bold uppercase tracking-widest text-amber-600">Step {s.n}</div>
                <div className="mt-1 font-display text-base font-extrabold text-navy-deep">{s.t}</div>
                <div className="mt-1 text-[12px] text-slate-500">{s.d}</div>
              </li>
            ))}
          </ol>
        </section>

        <section className="mt-12 rounded-3xl border border-amber-200 bg-gradient-to-br from-amber-50 to-white p-6 sm:p-8">
          <div className="text-[11px] font-bold uppercase tracking-widest text-amber-700">Pricing</div>
          <h2 className="mt-1 font-display text-3xl font-extrabold text-navy-deep sm:text-4xl">
            {label} → {c.nameEn} from THB {c.priceFromTh.toLocaleString()}
          </h2>
          <ul className="mt-5 grid gap-2.5 sm:grid-cols-2">
            <li className="flex items-start gap-2 text-sm text-slate-700"><ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" /> Includes MoJ-registered translator</li>
            <li className="flex items-start gap-2 text-sm text-slate-700"><Clock className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" /> Express 2 business days +THB 2,500</li>
            <li className="flex items-start gap-2 text-sm text-slate-700"><ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" /> MFA / Embassy government fees billed at cost</li>
            <li className="flex items-start gap-2 text-sm text-slate-700"><ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" /> Worldwide EMS / local Grab delivery</li>
          </ul>
        </section>

        {others.length > 0 && (
          <section className="mt-14 border-t border-slate-200 pt-10">
            <h2 className="font-display text-2xl font-extrabold text-navy-deep sm:text-3xl">Other destinations in the region</h2>
            <div className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {others.map((o) => (
                <Link key={o.slug} to="/en/apostille/$country" params={{ country: o.slug }}
                  className="group flex items-center gap-3 rounded-2xl border border-slate-200 bg-white p-4 transition hover:-translate-y-0.5 hover:border-gold/50 hover:shadow-md">
                  <div className="text-2xl">{o.flag}</div>
                  <div className="flex-1">
                    <div className="font-display text-sm font-extrabold text-navy-deep group-hover:text-amber-600">{o.nameEn}</div>
                    <div className="mt-0.5 text-[11px] text-slate-500">{o.isHague ? "Apostille" : "Legalization"} · from THB {o.priceFromTh.toLocaleString()}</div>
                  </div>
                </Link>
              ))}
            </div>
          </section>
        )}

        <section className="mt-14 border-t border-slate-200 pt-10 text-sm text-slate-600">
          Looking for the Thai version? <Link to="/apostille/$country" params={{ country: slug }} className="font-semibold text-amber-700 underline">/apostille/{slug}</Link>
        </section>

        <ApostilleReverseLinks mode="en" apostilleSlug={c.slug} countryNameTh={c.nameTh} countryNameEn={c.nameEn} isHague={c.isHague} />
        <ApostilleTripleKeyLinks mode="en" countryNameTh={c.nameTh} countryNameEn={c.nameEn} isHague={c.isHague} />
      </div>

      <CountryDeepContent lang="en" service="apostille" slug={c.slug} countryTh={c.nameTh} countryEn={c.nameEn} flag={c.flag} hague={c.isHague} authority={c.authority} priceFromTh={c.priceFromTh} turnaround={c.turnaround} />
      <TrustLayer variant="compact" />
          <QuotableStatsStrip cluster="apostille" locale="en" variant="light" />
    </main>
  );
}
